import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Spinner,
  Alert,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
} from 'reactstrap';
import PasskeyRegistration from './PasskeyRegistration';
import {
  listPasskeys,
  updatePasskeyNickname,
  deletePasskey,
} from '../../services/passkeyService';

const PasskeyManager = () => {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editNickname, setEditNickname] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [passkeyToDelete, setPasskeyToDelete] = useState(null);

  useEffect(() => {
    loadPasskeys();
  }, []);

  const loadPasskeys = async () => {
    try {
      setLoading(true);
      const keys = await listPasskeys();
      setPasskeys(keys);
      setError(null);
    } catch (err) {
      console.error('Failed to load passkeys:', err);
      setError('Failed to load passkeys. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationSuccess = (newPasskey) => {
    // Reload passkeys list
    loadPasskeys();
  };

  const handleEditClick = (passkey) => {
    setEditingId(passkey.id);
    setEditNickname(passkey.nickname || '');
  };

  const handleSaveNickname = async (passkeyId) => {
    try {
      await updatePasskeyNickname(passkeyId, editNickname.trim());
      setEditingId(null);
      setEditNickname('');
      await loadPasskeys();
    } catch (err) {
      console.error('Failed to update nickname:', err);
      setError('Failed to update nickname. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditNickname('');
  };

  const handleDeleteClick = (passkey) => {
    setPasskeyToDelete(passkey);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!passkeyToDelete) return;

    try {
      await deletePasskey(passkeyToDelete.id);
      setDeleteModalOpen(false);
      setPasskeyToDelete(null);
      await loadPasskeys();
    } catch (err) {
      console.error('Failed to delete passkey:', err);
      setError('Failed to delete passkey. Please try again.');
      setDeleteModalOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setPasskeyToDelete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDeviceIcon = (deviceType) => {
    const type = (deviceType || '').toLowerCase();
    if (type.includes('iphone') || type.includes('ios')) return '📱';
    if (type.includes('android')) return '📱';
    if (type.includes('mac')) return '💻';
    if (type.includes('windows')) return '💻';
    if (type.includes('linux')) return '💻';
    return '🔐';
  };

  return (
    <Card className="passkey-manager">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <div>
          <h5 className="mb-0">Passkeys</h5>
          <small className="text-muted">
            Gestiona tus métodos de autenticación sin contraseña
          </small>
        </div>
        <PasskeyRegistration
          onSuccess={handleRegistrationSuccess}
          buttonText="Agregar Nueva Passkey"
          buttonProps={{ size: 'sm' }}
        />
      </CardHeader>
      <CardBody>
        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Cargando passkeys...</p>
          </div>
        ) : passkeys.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted mb-3">Aún no has registrado ninguna passkey.</p>
            <p className="small text-muted">
              Las passkeys proporcionan autenticación segura sin contraseña usando la biometría
              de tu dispositivo o PIN.
            </p>
          </div>
        ) : (
          <Table hover responsive>
            <thead>
              <tr>
                <th>Dispositivo</th>
                <th>Nombre</th>
                <th>Creado</th>
                <th>Último Uso</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {passkeys.map((passkey) => (
                <tr key={passkey.id}>
                  <td>
                    <span className="device-icon me-2">
                      {getDeviceIcon(passkey.device_type)}
                    </span>
                    <span className="small text-muted">{passkey.device_type || 'Unknown'}</span>
                  </td>
                  <td>
                    {editingId === passkey.id ? (
                      <Input
                        type="text"
                        value={editNickname}
                        onChange={(e) => setEditNickname(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveNickname(passkey.id);
                          }
                        }}
                        autoFocus
                        bsSize="sm"
                      />
                    ) : (
                      <strong>{passkey.nickname || 'Passkey sin nombre'}</strong>
                    )}
                  </td>
                  <td className="small text-muted">{formatDate(passkey.created_at)}</td>
                  <td>
                    {passkey.last_used_at ? (
                      <Badge color="success" pill>
                        {formatDate(passkey.last_used_at)}
                      </Badge>
                    ) : (
                      <Badge color="secondary" pill>
                        Nunca
                      </Badge>
                    )}
                  </td>
                  <td>
                    {editingId === passkey.id ? (
                      <>
                        <Button
                          color="success"
                          size="sm"
                          onClick={() => handleSaveNickname(passkey.id)}
                          className="me-1"
                        >
                          Guardar
                        </Button>
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={handleCancelEdit}
                        >
                          Cancelar
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          color="primary"
                          size="sm"
                          outline
                          onClick={() => handleEditClick(passkey)}
                          className="me-1"
                        >
                          Renombrar
                        </Button>
                        <Button
                          color="danger"
                          size="sm"
                          outline
                          onClick={() => handleDeleteClick(passkey)}
                        >
                          Eliminar
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <div className="mt-3 p-3 bg-light rounded">
          <h6 className="mb-2">Acerca de las Passkeys</h6>
          <ul className="small text-muted mb-0">
            <li>Las passkeys usan la biometría de tu dispositivo (huella digital, Face ID) o PIN para autenticación</li>
            <li>Son más seguras que las contraseñas y no pueden ser robadas mediante phishing</li>
            <li>Cada passkey es única para este dispositivo</li>
            <li>Puedes tener múltiples passkeys para diferentes dispositivos</li>
          </ul>
        </div>
      </CardBody>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete}>
        <ModalHeader toggle={handleCancelDelete}>
          Eliminar Passkey
        </ModalHeader>
        <ModalBody>
          {passkeyToDelete && (
            <>
              <p>¿Estás seguro de que deseas eliminar esta passkey?</p>
              <div className="p-3 bg-light rounded">
                <strong>{passkeyToDelete.nickname || 'Passkey sin nombre'}</strong>
                <br />
                <small className="text-muted">
                  {passkeyToDelete.device_type} • Creado {formatDate(passkeyToDelete.created_at)}
                </small>
              </div>
              <Alert color="warning" className="mt-3 mb-0">
                <small>
                  No podrás usar esta passkey para iniciar sesión nuevamente. Esta acción no se puede deshacer.
                </small>
              </Alert>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCancelDelete}>
            Cancelar
          </Button>
          <Button color="danger" onClick={handleConfirmDelete}>
            Eliminar Passkey
          </Button>
        </ModalFooter>
      </Modal>

      <style>{`
        .passkey-manager .device-icon {
          font-size: 1.2rem;
        }

        .passkey-manager .table td {
          vertical-align: middle;
        }

        .passkey-manager .bg-light {
          background-color: #f8f9fa !important;
        }
      `}</style>
    </Card>
  );
};

export default PasskeyManager;
